// Short-link deep-link resolution (e.g. hogc.link/nfccard).
// Ported from hogc-one-mobile's deeplink util, adapted for gens conventions.
//
// Universal/app links hand the app the SHORT url — the OS intercepts the tap
// before any HTTP request, so the short.io redirect never runs. The slug is
// resolved against the gens API's DeepLinkConfig table instead, which keeps
// the mapping editable (internal tool) without app releases.

import axios from 'axios';
import { extractConfTypeFromPath, setConferenceView } from './conference-type';

const DEFAULT_SHORT_LINK_HOSTS = ['hogc.link'];
const INVALID_ROUTE_PATH_NAMES = new Set(['undefined', 'null']);
const HOME_ROUTE = { path: '/', query: {}, hash: '' };

export const getShortLinkHosts = () => {
  const raw = process.env.VUE_APP_SHORT_LINK_HOSTS;
  if (!raw) return DEFAULT_SHORT_LINK_HOSTS;
  return raw
    .split(',')
    .map(host => host.trim())
    .filter(Boolean);
};

export const isShortLinkUrl = url => {
  let urlObj;
  try {
    urlObj = new URL(url);
  } catch (e) {
    return false;
  }
  if (urlObj.protocol !== 'https:' && urlObj.protocol !== 'http:') return false;
  return getShortLinkHosts().includes(urlObj.hostname);
};

export const fetchDeepLinkPath = async slug => {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/deeplink/${encodeURIComponent(slug)}`,
  );
  return data;
};

export const normalizeDeepLinkPath = path => {
  if (typeof path !== 'string') return '';

  const trimmedPath = path.trim();
  if (!trimmedPath) return '';

  const pathName = trimmedPath.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (INVALID_ROUTE_PATH_NAMES.has(pathName)) return '';

  return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
};

const buildRouteFromResolvedPath = (resolvedPath, originalUrlObj) => {
  const query = {};
  for (const [key, value] of originalUrlObj.searchParams.entries()) {
    query[key] = value;
  }
  return { path: resolvedPath, query, hash: originalUrlObj.hash };
};

/**
 * Route a short-link URL (hogc.link/…) to its mapped in-app path.
 * Mirrors redirectDeepLinkPath's contract: returns true when it handled the
 * URL (router.push done), false when the URL is not a short link.
 */
export const redirectShortLinkPath = async (router, url, store = null) => {
  if (!isShortLinkUrl(url)) return false;

  const urlObj = new URL(url);
  // pathname is already percent-encoded; decode so fetchDeepLinkPath's
  // encodeURIComponent doesn't double-encode.
  let slug = urlObj.pathname.replace(/^\/+|\/+$/g, '');
  try {
    slug = decodeURIComponent(slug);
  } catch (e) {
    // malformed escape sequence — keep the raw slug
  }
  if (!slug) {
    router.push(HOME_ROUTE);
    return true;
  }

  let resolvedPath = '';
  try {
    const deepLink = await fetchDeepLinkPath(slug);
    resolvedPath = normalizeDeepLinkPath(deepLink?.path);
  } catch (e) {
    console.log('redirectShortLinkPath: lookup failed', e?.message);
  }

  if (!resolvedPath) {
    router.push(HOME_ROUTE);
    return true;
  }

  // Same conference-mode handling as redirectDeepLinkPath — only when the
  // resolved path has an explicit variant prefix (/gpcr/*, /gic/*).
  const confType = extractConfTypeFromPath(resolvedPath);
  if (confType) {
    setConferenceView(confType);
    // This is how a newly-granted conference access is actually discovered in
    // practice (EDM click-through). The router's mode-drives-route guard
    // resolves confType from store.state.profile.conferences, which is
    // memoized for the whole app session — without a fresh fetch here, a
    // stale cache would silently bounce this link back to the old default
    // instead of landing on the newly-accessible conference.
    if (store?.state?.profile?.name) {
      await store.dispatch('profile/forceFetchProfile');
    }
  }

  const routeObj = buildRouteFromResolvedPath(resolvedPath, urlObj);
  sessionStorage.setItem('deepLinkPathObj', JSON.stringify(routeObj));
  router.push(routeObj);
  return true;
};
